import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, ArrowRight, Code, Webhook } from "lucide-react";
import ScrollReveal from "@/components/ui/scroll-reveal";
import { useTranslation } from "@/hooks/useTranslation";

const Integrations = () => {
  const { t } = useTranslation();

  const integrations = [
    {
      name: "Make",
      logo: "/6800854c27145f7367a32df5_Make-app-icon.png",
      title: t("integrations.cards.make.title"),
      description: t("integrations.cards.make.description")
    },
    {
      name: "Twilio",
      logo: "/67ecc7817512727dc8c98d5a_twilio logo 22.webp",
      title: t("integrations.cards.twilio.title"),
      description: t("integrations.cards.twilio.description")
    },
    {
      name: "WhatsApp",
      logo: "/681651a176e3390d4b60b237_whatsapp-icon-free-png-p-500.webp",
      title: t("integrations.cards.whatsapp.title"),
      description: t("integrations.cards.whatsapp.description")
    },
    {
      name: "Google Calendar",
      logo: "/681651a1770af1e5412149bc_Google_Calendar_icon_(2020).svg-p-500.png",
      title: t("integrations.cards.googleCalendar.title"),
      description: t("integrations.cards.googleCalendar.description")
    },
    {
      name: "Zapier",
      logo: "/67ecc7817512727dc8c98d60_zapier-logo-png-transparent-p-500.png",
      title: t("integrations.cards.zapier.title"),
      description: t("integrations.cards.zapier.description")
    },
    {
      name: "APIs & Webhooks",
      logo: "/67ecc7817512727dc8c98d66_Untitled design - 2025-04-02T003851.028.png",
      title: t("integrations.cards.apis.title"),
      description: t("integrations.cards.apis.description")
    }
  ];

  return (
    <section className="py-12 md:py-20 bg-background">
      <div className="container mx-auto px-4">
        <ScrollReveal>
          <div className="text-center max-w-3xl mx-auto mb-10 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 md:mb-4 text-foreground">
              {t("integrations.title")}
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground px-2">
              {t("integrations.subtitle")}
            </p>
          </div>
        </ScrollReveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {integrations.map((integration, index) => (
            <ScrollReveal key={index} delay={index * 100}>
              <div className="bg-card rounded-xl p-5 sm:p-6 md:p-8 h-full flex flex-col shadow-md border border-border hover:shadow-lg transition-shadow">
                <div className="mb-4 md:mb-6 flex justify-center">
                  {integration.name === "APIs & Webhooks" ? (
                    <div className="h-12 w-12 sm:h-16 sm:w-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                      <Code className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                    </div>
                  ) : integration.name === "Twilio" ? (
                    <img 
                      src="/twilio-icon.svg" 
                      alt="Twilio logo" 
                      className="h-12 w-12 sm:h-16 sm:w-16 object-contain"
                    />
                  ) : (
                    <img 
                      src={integration.logo} 
                      alt={`${integration.name} logo`} 
                      className="h-12 w-12 sm:h-16 sm:w-16 object-contain"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const parent = target.parentElement;
                        if (parent) {
                          parent.innerHTML = `<div class="h-12 w-12 sm:h-16 sm:w-16 bg-muted rounded-lg flex items-center justify-center"><span class="text-muted-foreground font-semibold">${integration.name.charAt(0)}</span></div>`;
                        }
                      }}
                    />
                  )}
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 md:mb-3 text-foreground text-center">
                  {integration.title}
                </h3>
                <p className="text-sm sm:text-base text-muted-foreground mb-4 text-center flex-grow">
                  {integration.description}
                </p>
              </div>
            </ScrollReveal>
          ))}
        </div>

        <ScrollReveal delay={300}>
          <div className="mt-10 md:mt-16 bg-gradient-to-r from-brand-50 to-brand-100 dark:from-brand-900/20 dark:to-brand-800/20 rounded-xl p-6 sm:p-8 md:p-12 border border-brand-200 dark:border-brand-700 shadow-lg">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 items-center">
              <div>
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 md:mb-4 text-foreground">
                  {t("integrations.cta.title")}
                </h3>
                <p className="text-sm sm:text-base text-muted-foreground mb-4 md:mb-6">
                  {t("integrations.cta.subtitle")}
                </p>
                
                <h4 className="text-lg sm:text-xl font-semibold text-foreground mb-2 md:mb-3">{t("integrations.cta.optimize")}</h4>
                <p className="text-sm sm:text-base text-muted-foreground mb-4 md:mb-6">
                  {t("integrations.cta.optimizeDesc")}
                </p>
                
                <Link to="/demo">
                  <Button className="bg-brand-500 hover:bg-brand-600 text-white py-5 sm:py-6 px-6 sm:px-8 rounded-lg w-full sm:w-auto">
                    {t("integrations.cta.button")}
                  </Button>
                </Link>
                
                <div className="flex flex-col sm:flex-row gap-4 sm:gap-8 mt-6 md:mt-8">
                  <div className="flex items-center gap-2">
                    <Check className="text-white bg-brand-500 rounded-full p-1 w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
                    <span className="text-sm sm:text-base text-muted-foreground">{t("integrations.cta.trial")}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="text-white bg-brand-500 rounded-full p-1 w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
                    <span className="text-sm sm:text-base text-muted-foreground">{t("integrations.cta.credit")}</span>
                  </div>
                </div>
              </div>
              
              <div className="relative hidden lg:block">
                <div className="bg-white/80 dark:bg-background/80 p-4 rounded-xl flex items-center justify-center shadow-md">
                  <img 
                    src="/6800854c27145f7367a32df5_Make-app-icon.png" 
                    alt="Make logo" 
                    className="h-24 w-24 md:h-36 md:w-36 object-contain rounded-full bg-white p-4 shadow-sm"
                  />
                </div>
                
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full flex items-center justify-center">
                  <div className="relative w-full h-full">
                    <div className="absolute right-0 top-0 transform translate-x-1/4 -translate-y-1/4">
                      <img
                        src="/67ecc7817512727dc8c98d60_zapier-logo-png-transparent-p-500.png"
                        alt="Zapier logo"
                        className="h-20 w-20 md:h-32 md:w-32 object-contain bg-white rounded-lg p-2 shadow-md"
                      />
                    </div>
                    
                    <div className="absolute left-0 bottom-0 transform -translate-x-1/4 translate-y-1/4">
                      <div className="h-20 w-20 md:h-32 md:w-32 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg p-2 shadow-md flex items-center justify-center">
                        <Webhook className="h-10 w-10 md:h-16 md:w-16 text-white" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ScrollReveal>
        
        <div className="mt-6 md:mt-8 text-center">
          <Link to="/contact">
            <Button variant="outline" className="mx-auto border-border text-foreground hover:bg-muted">
              {t("integrations.customIntegration")} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Integrations;
